import { crudActions, crudMutations, crudGetters } from "@/utils/store";
import {
  PUBLIC_AUTHOR_ROLES,
  authorContributionTotal,
  isPublicAuthor,
  userDisplayName,
} from "@/utils/userPublic.js";

const BASE_URL = `${process.env.BASE_URL_V2}/users/`
const BASE_URL_V2 = `${process.env.BASE_URL_V2}/users/`
const AUTHORS_PAGE_SIZE = 100

export const state = () => ({
  objects: [],
  total: 0
})

export const mutations = { ...crudMutations }

export const actions = {
  ...crudActions(BASE_URL),
  async getAdminStats({ commit }, params = {}) {
    return this.$axios.$get(`/admin/stats/`, { params: params })
  },
  async getPublicStats({ commit }, params = {}) {
    return this.$axios.$get(`/public/stats/`, { params: params })
  },
  async searchUsers({}, params = {}) {
    const searchParams = {
      limit: params.limit || 10,
      ...params
    }
    return this.$axios.$get(`${BASE_URL}`, { params: searchParams })
  },
  async fetchPublicAuthorsTable({}, params = {}) {
    const base = process.env.BASE_URL_V2
    return this.$axios.$get(`${base}/public/stats/authors/`, { params })
  },
  async fetchPublicCategoryAuthorsTable({}, params = {}) {
    const base = process.env.BASE_URL_V2
    return this.$axios.$get(`${base}/public/stats/category-authors/`, { params })
  },
  async fetchCategoryTopExperts({ dispatch }, params = {}) {
    const limit = params.limit || 4
    const query = {
      limit,
      offset: params.offset || 0,
      order: params.order || '-materials_total',
    }
    if (params.category != null && params.category !== '') {
      query.category_id = params.category
    }

    const response = await dispatch('fetchPublicCategoryAuthorsTable', query)
    const rows = (response.items || [])
      .filter((row) => authorContributionTotal(row) > 0)

    if (!rows.length) {
      return []
    }

    return dispatch('enrichAuthorsWithProfiles', rows)
  },
  async fetchTopExperts({ dispatch }, params = {}) {
    const limit = params.limit || 4
    const query = {
      limit: params.tableLimit || 100,
      offset: params.offset || 0,
    }
    if (params.category != null && params.category !== '') {
      query.most_freq_category_id = params.category
    }

    const response = await dispatch('fetchPublicAuthorsTable', query)
    const rows = (response.items || [])
      .filter((row) => authorContributionTotal(row) > 0)
      .sort((a, b) => authorContributionTotal(b) - authorContributionTotal(a))
      .slice(0, limit)

    if (!rows.length) {
      return []
    }

    return dispatch('enrichAuthorsWithProfiles', rows)
  },
  async fetchPublicAuthorStats({ dispatch }, authorId) {
    const response = await dispatch('fetchPublicAuthorsTable', { limit: 500, offset: 0 })
    const row = (response.items || []).find((r) => String(r.author_id) === String(authorId))
    return row || null
  },
  async enrichAuthorsWithProfiles({ dispatch }, rows = []) {
    const ids = [...new Set(rows.map((r) => r.author_id).filter(Boolean))]
    if (!ids.length) return rows

    const chunkSize = 50
    const profilesById = {}

    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize)
      const response = await dispatch('searchUsers', {
        ids: chunk.join(','),
        limit: chunk.length,
      })
      ;(response.items || []).forEach((u) => {
        profilesById[String(u.id)] = u
      })
    }

    return rows.map((row) => ({
      ...row,
      profileUser: profilesById[String(row.author_id)] || null,
    }))
  },
  async fetchPublicAuthors({}, params = {}) {
    const roles = params.roles || PUBLIC_AUTHOR_ROLES
    const roleSet = new Set(roles)
    const limit = params.limit || AUTHORS_PAGE_SIZE
    let offset = 0
    let total = Infinity
    const items = []

    while (offset < total) {
      const response = await this.$axios.$get(`${BASE_URL}`, {
        params: { limit, offset, ...params.query },
      })
      total = response.total ?? 0
      const batch = (response.items || []).filter((u) => roleSet.has(u.role) && isPublicAuthor(u))
      items.push(...batch)
      offset += limit
      if (!(response.items || []).length) break
    }

    items.sort((a, b) => userDisplayName(a).localeCompare(userDisplayName(b), 'ru'))
    return { items, total: items.length }
  },
  async fetchPublicProfile({}, id) {
    return this.$axios.$get(`${BASE_URL_V2}${id}/`)
  },
}

export const getters = { ...crudGetters }
